import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchFilter } from '../../../store/ai2';
import type { RootState } from '../../../store';
import { Container } from 'react-bootstrap';
import { QuickSearchBadge } from '../Basics/QuickSearchBadge';
import './filter.scss';
import { useForm } from 'react-hook-form';
import { useFilterOptions, type Option } from './hooks/useFilterOptions';
import { insightMapping } from './utils/insightMapping';
import SelectFilter from './components/SelectFilter';
import InputFilter from './components/InputFilter';
import ActionButtons from './components/ActionButtons';

type EpisodeOption = Option | null;

interface FormValues {
  clinics: Option[];
  primaryTeams: Option[];
  patientName: string;
  episodeStatus: EpisodeOption;
  healthProfessionals: Option[];
  insightCategory: Option[];
  tags: Option[];
  favourite: boolean;
  assigned?: string | number | undefined;
  event_subcategory_name: string;
}

interface SubmitValues
  extends Omit<
    FormValues,
    | 'clinics'
    | 'episodeStatus'
    | 'healthProfessionals'
    | 'insightCategory'
    | 'primaryTeams'
    | 'tags'
  > {
  clinics: string[];
  episodeStatus: string;
  healthProfessionals: string[];
  insightCategory: Option[];
  primaryTeams: string[];
  tags: string[];
}

const FilterComponent: React.FC = () => {
  const dispatch = useDispatch();
  const { allTenancyClinics, selectedTenancy, clinicianId } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const [selectedPhrase, setSelectedPhrase] = useState<string | null>(null);
  const searchToHide = selectedTenancy?.features?.patientListHide as
    | string[]
    | undefined;
  const quickSearchMap = selectedTenancy?.features?.quickSearchSubcatMap as
    | Record<string, string>
    | undefined;

  const { register, watch, getValues, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      clinics: [],
      primaryTeams: [],
      patientName: '',
      episodeStatus: null,
      healthProfessionals: [],
      insightCategory: [],
      tags: [],
      favourite: false,
      assigned: undefined,
      event_subcategory_name: '',
    },
  });

  const quickSearchPhrases: string[] =
    (selectedTenancy?.features?.quickSearchOptions as string[]) || [];
  const insightCategory =
    (selectedTenancy?.features?.insightCategory as Record<
      string,
      string | string[]
    >) || {};

  const filterOptions = useFilterOptions({
    allTenancyClinics,
    selectedTenancy,
    insightCategory,
  });

  const getFormValues = (): SubmitValues => {
    const currentValues = getValues();

    return {
      ...currentValues,
      clinics: currentValues.clinics.map((item) => item.value),
      episodeStatus: currentValues.episodeStatus?.value || '',
      healthProfessionals: currentValues.healthProfessionals.map(
        (item) => item.value,
      ),
      insightCategory: currentValues.insightCategory.map((m) =>
        insightMapping[m.value]
          ? { label: m.label, value: insightMapping[m.value] }
          : m,
      ),
      primaryTeams: currentValues.primaryTeams.map((item) => item.value),
      tags: currentValues.tags.map((item) => item.value),
    };
  };

  const refreshFilter = () => {
    reset();
    dispatch(setSearchFilter(getFormValues()));
  };

  const onResetForm = () => {
    refreshFilter();
    setSelectedPhrase(null);
  };

  useEffect(() => {
    refreshFilter();
  }, [selectedTenancy]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'patientName') {
      setValue('patientName', value);
    }
  };

  const filterField = (
    field: keyof FormValues,
    selectedOptions: Option[] | Option | null,
  ) => {
    if (typeof field !== 'string') {
      return;
    }
    setValue(field as any, selectedOptions as any);
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = { ...getFormValues() };

    const searchFilters = {
      ...values,
      insightCategory: values.insightCategory.map((m) =>
        insightMapping[m.value] ? insightMapping[m.value] : m.value,
      ),
    };
    dispatch(setSearchFilter(searchFilters));
  };

  const handlePhraseClick = (phrase: string) => {
    onResetForm();
    const isRemove = phrase === selectedPhrase;
    const newPhrase = isRemove ? null : phrase;
    setSelectedPhrase(newPhrase);

    switch (phrase) {
      case 'Patients assigned to me':
        setValue('assigned', isRemove ? undefined : (clinicianId as any));
        break;
      case 'Starred patients':
        setValue('favourite', isRemove ? false : true);
        break;
      default:
        setValue(
          'event_subcategory_name',
          isRemove ? '' : quickSearchMap ? quickSearchMap[phrase] : '',
        );
        break;
    }
    dispatch(setSearchFilter(getFormValues()));
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="filter-container">
        <Container fluid>
          <div className="filterBg">
            <div id="filterLine1">
              <SelectFilter
                label={'Service'}
                registerReturn={register('clinics')}
                isMulti={true}
                options={filterOptions.clinicOptions}
                value={watch('clinics')}
                placeholder={'All'}
                showTick={true}
                onChange={(selectedOptions) =>
                  filterField('clinics', selectedOptions as Option[])
                }
                onClear={() => {
                  setValue('clinics', []);
                }}
              />

              <SelectFilter
                label={'Primary teams'}
                registerReturn={register('primaryTeams')}
                isMulti={true}
                options={filterOptions.ptOptions}
                value={watch('primaryTeams')}
                placeholder={'All'}
                showTick={true}
                onChange={(selectedOptions) =>
                  filterField('primaryTeams', selectedOptions as Option[])
                }
                onClear={() => {
                  setValue('primaryTeams', []);
                }}
              />

              <SelectFilter
                label={'Health professionals'}
                registerReturn={register('healthProfessionals')}
                isMulti={true}
                options={filterOptions.hpOptions}
                value={watch('healthProfessionals')}
                placeholder={'All'}
                showTick={true}
                onChange={(selectedOptions) =>
                  filterField(
                    'healthProfessionals',
                    selectedOptions as Option[],
                  )
                }
                onClear={() => {
                  setValue('healthProfessionals', []);
                }}
              />

              <SelectFilter
                label="Insight category"
                registerReturn={register('insightCategory')}
                isMulti={true}
                options={filterOptions.fcOptions}
                value={watch('insightCategory')}
                placeholder={'All'}
                showTick={true}
                onChange={(selectedOptions) =>
                  filterField('insightCategory', selectedOptions as Option[])
                }
                onClear={() => {
                  setValue('insightCategory', []);
                }}
              />
              {(!searchToHide || !searchToHide.includes('Episode')) && (
                <SelectFilter
                  label={'Episode status'}
                  registerReturn={register('episodeStatus')}
                  isMulti={false}
                  isClearable={true}
                  options={filterOptions.esOptions}
                  value={watch('episodeStatus')}
                  placeholder={'All'}
                  showTick={true}
                  onChange={(selectedOptions) =>
                    filterField(
                      'episodeStatus',
                      selectedOptions as Option | null,
                    )
                  }
                  onClear={() => {
                    setValue('episodeStatus', null);
                  }}
                />
              )}

              <SelectFilter
                label={'Tags'}
                registerReturn={register('tags')}
                isMulti={true}
                options={filterOptions.tagOptions}
                value={watch('tags')}
                placeholder={'All'}
                showTick={true}
                onChange={(selectedOptions) =>
                  filterField('tags', selectedOptions as Option[])
                }
                onClear={() => {
                  setValue('tags', []);
                }}
              />

              <InputFilter
                label={'Patient name'}
                registerReturn={register('patientName')}
                inputProps={{
                  type: 'text',
                  placeholder: '',
                  name: 'patientName',
                  id: 'patientName',
                }}
                onChange={onInputChange}
              />

              <ActionButtons
                onPrimaryClick={() => {}}
                onSecondaryClick={onResetForm}
                primaryText={'Show results'}
                secondaryText={'Clear'}
              />
            </div>

            {quickSearchPhrases?.length > 0 && (
              <div className="quickSearch">
                <span className="quickSearchTitle">Quick searches:</span>
                {quickSearchPhrases.map((phrase, index) => (
                  <QuickSearchBadge
                    key={index + '' + phrase}
                    text={phrase}
                    quickSearchSelected={phrase === selectedPhrase}
                    onClick={() => handlePhraseClick(phrase)}
                  />
                ))}
              </div>
            )}
          </div>
          <div
            id="filterLine2"
            style={{ paddingLeft: '70px', paddingRight: '70px' }}
          >
            <div className="heading3">Patient search results</div>
          </div>
        </Container>
      </div>
    </form>
  );
};

export { FilterComponent };
